import { OverlayContainer } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { Platform } from '@angular/cdk/platform';
import { DropdownOption } from './dropdown.interface';

@Component({
  selector: 'osn-shared-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DropdownComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() public options: DropdownOption<any>[];
  @Input() public translateOptions = false;
  @Input() public defaultSelectedValue: any;
  @Output() public seasonSelect = new EventEmitter<string | number>();

  public isMobile: boolean;
  public isSelectOpened = false;
  public selectedIndex: number;
  public get selectedValue(): any {
    return this.options[this.selectedIndex]?.value;
  }
  public onChanged: any = () => {};
  public onTouched: any = () => {};
  @ViewChild('matSelect') public matSelect: MatSelect;
  @HostListener('document:scroll') public onScroll() {
    if (this.isMobile && this.isSelectOpened) {
      this.matSelect.close();
      this.isSelectOpened = false;
    }
  }

  public constructor(private readonly overlayContainer: OverlayContainer, public platform: Platform) {
    if (platform.ANDROID || platform.IOS) {
      this.isMobile = true;
    }
  }

  public ngOnInit() {
    if (this.defaultSelectedValue) {
      this.selectedIndex = this.getIndexByValue(this.defaultSelectedValue);
    }
  }

  public ngOnChanges(changes) {
    this.selectedIndex = this.getIndexByValue(changes.defaultSelectedValue?.currentValue ?? this.defaultSelectedValue);
  }

  public ngOnDestroy(): void {
    this.overlayContainer.getContainerElement().classList.remove('select-overlay');
  }

  public onSelectionChange(index: number) {
    if (!this.isMobile) {
      this.matSelect.close();
    }
    this.selectedIndex = Number(index);
    this.onChanged(this.selectedValue);
    this.onTouched();
    this.seasonSelect.emit(this.selectedValue);
  }

  public onAndroidSelectionChange(index: number) {
    this.selectedIndex = Number(index);
    this.onChanged(this.selectedValue);
    this.onTouched();
    this.seasonSelect.emit(this.selectedValue);
  }

  public initValue(value: any) {
    this.selectedIndex = this.getIndexByValue(value);
    this.onChanged(this.selectedValue);
    this.onTouched();
  }

  public writeValue(val: any): void {
    if (val && val !== this.selectedValue) {
      this.initValue(val);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChanged = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public onSelectOpen() {
    this.isSelectOpened = true;
    this.overlayContainer.getContainerElement().classList.add('select-overlay');
  }

  public onSelectBlur(event: FocusEvent) {
    if (!event.relatedTarget) {
      // we should close the selector when the page lose focus (e.g. user clicks into inspector)
      this.matSelect.close();
    }
    this.overlayContainer.getContainerElement().classList.remove('select-overlay');
    this.isSelectOpened = false;
  }

  private getIndexByValue(val: any): number {
    const currentIndex = this.options.findIndex((option) => option.value === val);
    return currentIndex === -1 ? 0 : currentIndex;
  }
}
export * from './dropdown.interface';
